import React from 'react';
import css from './StatusBar.module.scss';
import type { BasilStatusState } from '../types/basilTypes';

/** What the top bar says, and nothing more: no engine word ever reaches it. */
export type StatusBarProps = {
  /** The episode's title, taken from the script's own `# ` first line. */
  title: string;
  /** How many changes are staged; `no changes` when there are none. */
  changeCount: number;
  /** The status word, e.g. `reading…` or `ready`. Never `saved`, never a bare `verified`. */
  statusWord: string;
  /** The class the word is painted with. */
  statusState: BasilStatusState;
  /** Whether the context rail is showing. */
  contextOpen: boolean;
  onToggleContext: () => void;
  /** A live episode reads but never writes. */
  isReadOnly?: boolean;
  /**
   * How many staged changes can be sent and how many cannot, with the first blocking row's own
   * message. Present only while something blocks: a script is applied whole or not at all, so the
   * writer is told which change is holding the rest back rather than watching a subset go.
   */
  sendabilityNote?: string | null;
};

const changeWords = (count: number): string => {
  if (count <= 0) {
    return 'no changes';
  }

  return `${count} change${count === 1 ? '' : 's'}`;
};

const stateClass = (state: BasilStatusState): string => {
  if (state === 'working') {
    return css.statusWorking;
  }
  if (state === 'findings') {
    return css.statusFindings;
  }
  if (state === 'error') {
    return css.statusError;
  }

  return css.statusOk;
};

/**
 * One plain count and one status word. Everything the engine needs to say about a change — its
 * classification, the ids, the request body — lives in the Advanced footer and nowhere else
 * (editor.js:390-408).
 */
export function StatusBar(props: StatusBarProps) {
  const {
    title,
    changeCount,
    statusWord,
    statusState,
    contextOpen,
    onToggleContext,
    isReadOnly,
    sendabilityNote,
  } = props;

  return (
    <div className={css.bar}>
      <span className={css.title}>{title}</span>
      <span className={css.count} data-state={changeCount > 0 ? 'changed' : 'clean'}>
        {changeWords(changeCount)}
      </span>
      <span className={[css.status, stateClass(statusState)].join(' ')} role="status" aria-live="polite">
        {statusWord}
      </span>
      {sendabilityNote ? (
        <span className={css.sendability} role="status" aria-live="polite">{sendabilityNote}</span>
      ) : null}
      {isReadOnly ? <span className={css.readOnlyBadge}>Read only</span> : null}
      <button
        type="button"
        className={css.contextToggle}
        onClick={onToggleContext}
        aria-expanded={contextOpen}
      >
        Side panel
      </button>
    </div>
  );
}
